import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

/**
 * Pure helper — exported so unit tests can import it directly.
 * Returns true only when all conditions for playing a chime are met.
 */
export function shouldPlaySound(prev, current, armed, muted) {
  return current > prev && armed && !muted;
}

/**
 * Side-effect hook: plays /notification-chime.mp3 when unreadCount increases,
 * the user has interacted with the page (autoplay policy arm), and sound is not muted.
 *
 * Mute state is read from localStorage key `mc_notif_sound`:
 *   absent / anything other than 'off' → ON
 *   'off' → muted
 *
 * Returns nothing.
 */
function useNotificationSound() {
  const { unreadCount, prevUnreadCount } = useSelector((state) => ({
    unreadCount: state.Notifications.unreadCount,
    prevUnreadCount: state.Notifications.prevUnreadCount,
  }));

  const audioRef = useRef(null);
  const armedRef = useRef(false);

  // Create Audio instance once, inside an effect so jsdom environments
  // (unit tests) are not hit with ReferenceError: Audio is not defined.
  // Cleanup releases the element so it is not leaked if the layout unmounts.
  useEffect(() => {
    audioRef.current = new Audio('/notification-chime.mp3');
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Arm on first user interaction (satisfies autoplay policy).
  useEffect(() => {
    const arm = () => {
      armedRef.current = true;
      document.removeEventListener('pointerdown', arm);
      document.removeEventListener('click', arm);
      document.removeEventListener('keydown', arm);
    };

    document.addEventListener('pointerdown', arm);
    document.addEventListener('click', arm);
    document.addEventListener('keydown', arm);

    return () => {
      document.removeEventListener('pointerdown', arm);
      document.removeEventListener('click', arm);
      document.removeEventListener('keydown', arm);
    };
  }, []);

  // Play chime when conditions are met.
  useEffect(() => {
    if (!audioRef.current) return;
    const muted = localStorage.getItem('mc_notif_sound') === 'off';
    if (shouldPlaySound(prevUnreadCount, unreadCount, armedRef.current, muted)) {
      audioRef.current.play().catch(() => {});
    }
  }, [unreadCount, prevUnreadCount]);
}

export default useNotificationSound;
